import React, { useState, useRef, useEffect, useCallback } from 'react';
import Avatar from '../avatar';
import ChatBoxAvatar from '../avatar/chatboxAvatar';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { AVATAR_BORDER, CHAT_GROUP_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../global/constants';
import {
  Dropdown,
  Input,
  ItemsList,
  Option,
  OptionAvatar,
  OptionName,
  SelectContainer,
  SelectInput,
  Tag,
  TagAvatar,
  TagName
} from './styles';
import IconButton from '../button/iconButton';

interface UserInterface {
  id: string;
  name: string;
  email: string;
  phone: string;
  profile_photo?: string;
}

interface User {
  id: string;
  name: string;
  profile_photo?: string;
}

interface Group {
  id: string;
  name: string;
  type?: typeof CHAT_GROUP_TYPE.PROJECT | typeof CHAT_GROUP_TYPE.PERSONAL;
  users?: UserInterface[];
  profilePic?: string;
}

interface CustomSelectProps {
  users: User[];
  groups: Group[];
  currentUserId: string;
  value: string[];
  onChange: (value: string[], ref?: React.RefObject<HTMLInputElement>) => void;
  onBlur: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  users,
  groups,
  currentUserId,
  value,
  onChange,
  onBlur,
  placeholder = 'Enter name or Project name',
  autoFocus = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | FocusEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onBlur();
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusin', handleClickOutside);
    document.addEventListener('focusout', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusin', handleClickOutside);
      document.removeEventListener('focusout', handleClickOutside);
    };
  }, [onBlur]);

  const handleSelect = useCallback(
    (id: string) => {
      const newValue = [...value];
      const index = newValue.indexOf(id);
      if (index === -1) {
        newValue.push(id);
      } else {
        newValue.splice(index, 1);
      }
      setSearchTerm('');
      onChange(newValue);
    },
    [onChange, value]
  );

  const handleRemove = useCallback(
    (id: string) => {
      const newValue = value.filter((item) => item !== id);
      onChange(newValue);
      setSearchTerm('');
    },
    [onChange, value]
  );

  const filteredUsers = users.filter(
    (user) => user.id !== currentUserId && user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredGroups = groups.filter(
    (group) => group.users && group.users.length > 2 && group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderCloseIcon = useCallback(
    (id: string) => {
      return (
        <IconButton
          iconName='new-close-icon'
          size={ICON_BUTTON_SIZE.EXTRA_SMALL}
          variant={ICON_BUTTON_TYPE.TERTIARY}
          onClick={() => handleRemove(id)}
          type='rounded'
          className='close-icon-tag'
        />
      );
    },
    [handleRemove]
  );

  const renderTag = (id: string) => {
    const user = users.find((u) => u.id === id);
    const group = groups.find((g) => g.id === id);

    if (user) {
      return (
        <Tag key={id}>
          <TagAvatar>
            <Avatar
              imgSrc={user.profile_photo || ''}
              name={user.name || 'U N'}
              size={22}
              borderVariant={AVATAR_BORDER.NONE}
            />
            <TagName>{user.name}</TagName>
          </TagAvatar>
          {renderCloseIcon(id)}
        </Tag>
      );
    }

    if (group) {
      return (
        <Tag key={id}>
          <TagAvatar>
            {group.type === CHAT_GROUP_TYPE.PROJECT ? (
              <SVGIcon
                name='teamcamp-mobile-icon'
                width={ICON_SIZE.XL}
                height={ICON_SIZE.XL}
                viewBox='0 0 30 30'
                className='teamcamp-icon'
              />
            ) : (
              <ChatBoxAvatar imgSrc={group.profilePic || ''} name={group.name || ''} size={22} users={group.users} />
            )}
          </TagAvatar>
          <TagName>{group.name}</TagName>
          {renderCloseIcon(id)}
        </Tag>
      );
    }

    return null;
  };

  // Scroll selected option into view with improved behavior
  useEffect(() => {
    if (isOpen && optionRefs.current[currentIndex] && dropdownRef.current) {
      const selectedElement = optionRefs.current[currentIndex];
      const dropdownElement = dropdownRef.current;

      if (selectedElement) {
        const dropdownRect = dropdownElement.getBoundingClientRect();
        const selectedRect = selectedElement.getBoundingClientRect();

        // Check if the selected element is outside the visible area
        if (selectedRect.top < dropdownRect.top) {
          // Element is above the visible area, scroll up
          dropdownElement.scrollTop -= dropdownRect.top - selectedRect.top;
        } else if (selectedRect.bottom > dropdownRect.bottom) {
          // Element is below the visible area, scroll down
          dropdownElement.scrollTop += selectedRect.bottom - dropdownRect.bottom;
        }
      }
    }
  }, [currentIndex, isOpen]);

  // Reset currentIndex when dropdown closes or search term changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [isOpen, searchTerm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allOptions = [...filteredUsers, ...filteredGroups];

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setCurrentIndex((prevIndex) => (prevIndex === 0 ? allOptions.length - 1 : prevIndex - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setIsOpen(true);
          setCurrentIndex((prevIndex) => (prevIndex === allOptions.length - 1 ? 0 : prevIndex + 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (allOptions[currentIndex]) {
            const selectedItem = allOptions[currentIndex];
            handleSelect(selectedItem.id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          onBlur();
          break;
        default:
          break;
      }
    },
    [currentIndex, filteredUsers, filteredGroups, handleSelect, onBlur]
  );

  const handleMouseEnter = (index: number) => {
    setCurrentIndex(index);
  };

  const handleMouseLeave = () => {
    setCurrentIndex(-1); // or any other logic to reset focus
  };

  const isSelected = (id: string) => value.includes(id);

  return (
    <SelectContainer ref={containerRef}>
      <SelectInput role='button' onClick={() => setIsOpen(true)}>
        {value.map((id) => (
          <React.Fragment key={id}>{renderTag(id)}</React.Fragment>
        ))}
        <Input
          ref={inputRef}
          type='text'
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          autoFocus={autoFocus}
        />
      </SelectInput>

      <Dropdown
        ref={dropdownRef}
        isOpen={isOpen}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        tagsSelected={value.length > 0}>
        <ItemsList>
          {filteredUsers.map((user, index) => (
            <Option
              role='button'
              key={user.id}
              selected={isSelected(user.id)}
              focused={index === currentIndex}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleSelect(user.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(user.id);
                }
              }}>
              <OptionAvatar>
                <Avatar
                  imgSrc={user.profile_photo || ''}
                  name={user.name || 'U N'}
                  size={28}
                  borderVariant={AVATAR_BORDER.NONE}
                />
              </OptionAvatar>
              <OptionName>{user.name}</OptionName>
            </Option>
          ))}
          {filteredGroups.map((group, index) => (
            <Option
              role='button'
              key={`GROUP_${group.id}`}
              ref={(el) => (optionRefs.current[index + filteredUsers.length] = el)}
              selected={isSelected(group.id)}
              focused={index + filteredUsers.length === currentIndex}
              onMouseEnter={() => handleMouseEnter(index + filteredUsers.length)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleSelect(group.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(group.id);
                }
              }}>
              <OptionAvatar>
                {group.type === CHAT_GROUP_TYPE.PROJECT ? (
                  <SVGIcon
                    name='teamcamp-mobile-icon'
                    width={ICON_SIZE.XXXL}
                    height={ICON_SIZE.XXXL}
                    viewBox='0 0 30 30'
                    className='teamcamp-icon'
                  />
                ) : (
                  <ChatBoxAvatar
                    imgSrc={group.profilePic || ''}
                    name={group.name || ''}
                    size={28}
                    users={group.users}
                  />
                )}
              </OptionAvatar>
              <OptionName>{group.name}</OptionName>
            </Option>
          ))}
        </ItemsList>
      </Dropdown>
    </SelectContainer>
  );
};

export default CustomSelect;
